import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  PARCEL_LAYER_STYLE,
  PARCEL_POINTS_LAYER_STYLE,
  FILTER_HIGHLIGHT_STYLE,
  MY_LOTS_HIGHLIGHT_STYLE,
  LIST_SELECTED_LOT_HIGHLIGHT_STYLE,
  LIST_PENDING_ADD_HIGHLIGHT_STYLE,
  buildSelectedLotIdFilter,
  parcelSelectedGlowLayerId,
} from '../styles/mapTheme';
import {
  createEmptyParcelFilters,
  countActiveParcelFilters,
  getActiveFilterDefinitions,
  clearScopeUnsupportedFilters,
  clearListingFilters,
  clearOfficialUnitFilters,
  clearParcelFilters,
  countActiveOfficialUnitFilters,
} from '../data/parcelFilterContract';
import { buildParcelFilterMatchExpression } from '../data/parcelFilterMatch';

/**
 * Pertenencia a un conjunto de ids. `match` resuelve por tabla de búsqueda;
 * `in` sobre un literal recorre el array por cada feature, y estos conjuntos
 * llegan a miles de ids (alcance de lista, recorte de UF catastrales).
 * Ids como string para tolerar diferencias de tipo entre DB y tile; sin
 * duplicados porque `match` no admite etiquetas repetidas.
 */
function buildIdSetCondition(ids, emptySentinel) {
  const unique = Array.from(new Set(ids.map(String)));
  if (!unique.length) return ['==', ['get', 'id'], emptySentinel];
  return ['match', ['to-string', ['get', 'id']], unique, true, false];
}

/**
 * Condición MapLibre para acotar el match al alcance (mis registros / lista).
 * Con alcance activo pero sin ids (lista vacía) no matchea ninguna parcela.
 */
function buildScopeCondition(scopeIds, isScopeActive) {
  if (!isScopeActive) return null;
  return buildIdSetCondition(scopeIds, '__parcel-scope-empty__');
}

function buildLotIdsCondition(lotIds) {
  if (lotIds == null) return null;
  return buildIdSetCondition(lotIds, '__official-unit-empty__');
}

/**
 * Filtro de capa (setFilter): recorta qué polígonos del tile se pintan.
 * `showOnly` es “Mostrar solo filtradas”. `clipToHighlightIds` recorta el
 * tile a los lot_id de la lista: share con `show_cadastral_base: false`, o
 * dueño que oculta el catastro con una lista en el mapa.
 *
 * @param {{
 *   showOnly?: boolean,
 *   matchExpr?: object|null,
 *   clipToHighlightIds?: boolean,
 *   listIds?: string[],
 * }} options
 */
export function buildParcelLayerFilter({
  showOnly = false,
  matchExpr = null,
  clipToHighlightIds = false,
  listIds = [],
} = {}) {
  const filtered = showOnly && matchExpr ? matchExpr : null;
  if (!clipToHighlightIds) return filtered;
  const listOnly = buildIdSetCondition(
    Array.isArray(listIds) ? listIds : [],
    '__parcel-scope-empty__',
  );
  if (!filtered) return listOnly;
  return ['all', listOnly, filtered];
}

/**
 * useParcelFilters
 *
 * Filtros de parcelas sobre props del tile PMTiles (capa `parcelas` / `parcelas_puntos`).
 * Estado guiado por parcelFilterContract; expresiones MapLibre vía parcelFilterMatch.
 * Si hay un alcance activo (`scopeIds` + `isScopeActive`, ver useParcelFilterScope),
 * el match se restringe a esos ids con un AND adicional.
 * `scopeLockedByPanel`: Registros o detalle de lista bloquean el alcance en UI,
 * pero el mapa no se acota ni usa teselas de puntos hasta que haya filtros de atributos.
 */
export function useParcelFilters({
  mapInstanceRef,
  getInteractiveParcelLayerIds,
  getParcelPointsLayerIds,
  syncParcelPointsView,
  reapplyPointsFiltersRef,
  myLotsIds = [],
  isMyLotsOpen = false,
  listHighlightIds = [],
  isListsOpen = false,
  listSelectedLotId = null,
  listPendingAddIds = [],
  listSelectedLotHighlightStyle = null,
  scopeIds = [],
  isScopeActive = false,
  scopeLockedByPanel = false,
  officialUnitFiltersEnabled = true,
  officialUnitLotIds = null,
  /** Recorta el tile a la lista (share sin catastro de fondo, u “Ocultar lotes” con lista abierta). */
  clipToHighlightIds = false,
}) {
  const [activeFilters, setActiveFilters] = useState(createEmptyParcelFilters);
  const [showOnlyFiltered, setShowOnlyFiltered] = useState(false);

  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;

  const panelLockRef = useRef(scopeLockedByPanel);
  panelLockRef.current = scopeLockedByPanel;

  const idsRef = useRef({
    myLotsIds,
    listHighlightIds,
    listSelectedLotId,
    listPendingAddIds,
    listSelectedLotHighlightStyle,
  });
  idsRef.current = {
    myLotsIds,
    listHighlightIds,
    listSelectedLotId,
    listPendingAddIds,
    listSelectedLotHighlightStyle,
  };

  const scopeRef = useRef({ scopeIds, isScopeActive });
  scopeRef.current = { scopeIds, isScopeActive };

  const officialUnitLotIdsRef = useRef(officialUnitLotIds);
  officialUnitLotIdsRef.current = officialUnitLotIds;

  const myLotsIdsKey = useMemo(() => [...myLotsIds].sort().join('|'), [myLotsIds]);
  const listHighlightIdsKey = useMemo(() => [...listHighlightIds].sort().join('|'), [listHighlightIds]);
  const listPendingAddIdsKey = useMemo(
    () => [...listPendingAddIds].map(String).sort().join('|'),
    [listPendingAddIds],
  );
  const scopeIdsKey = useMemo(
    () => [...scopeIds].map(String).sort().join('|'),
    [scopeIds],
  );
  // A diferencia de los ids de alcance, acá no se arma una clave concatenada:
  // serían hasta 1000 UUID por respuesta. `useOfficialUnitMapFilter` garantiza
  // que la identidad del array solo cambia cuando cambia el contenido.

  const activeCount = countActiveParcelFilters(activeFilters);
  const hasActiveFilters = activeCount > 0;

  /**
   * Con un panel que fija el alcance (Registros / detalle de lista) el mapa no
   * se acota hasta que haya filtros de atributos: si no, las teselas de puntos
   * (una muestra) reemplazan los pins completos de los lotes del panel.
   */
  const shouldApplyScope = useCallback((filters) => {
    const scope = scopeRef.current;
    if (!scope.isScopeActive) return false;
    if (panelLockRef.current && countActiveParcelFilters(filters) === 0) return false;
    return true;
  }, []);

  const buildMatchExpression = useCallback((filters) => {
    const base = buildParcelFilterMatchExpression(filters);
    const scope = scopeRef.current;
    const scopeCondition = buildScopeCondition(scope.scopeIds, shouldApplyScope(filters));
    const officialCondition = buildLotIdsCondition(officialUnitLotIdsRef.current);
    const parts = [scopeCondition, officialCondition, base].filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0];
    return ['all', ...parts];
  }, [shouldApplyScope]);

  const buildPaintExpressions = useCallback((
    filters,
    myIds = [],
    myOpen = false,
    listIds = [],
    listsOpen = false,
    selectedListLotId = null,
    pendingAddIds = [],
    selectedLotHighlightStyle = null,
  ) => {
    const matchExpr = buildMatchExpression(filters);
    let fillColor, fillOpacity, lineColor, lineWidth;

    if (matchExpr) {
      const color = FILTER_HIGHLIGHT_STYLE.highlightColor;
      fillColor = ['case', matchExpr, color, PARCEL_LAYER_STYLE.fillColor];
      fillOpacity = ['case', matchExpr, FILTER_HIGHLIGHT_STYLE.fillOpacity, PARCEL_LAYER_STYLE.fillOpacity];
      lineColor = ['case', matchExpr, color, PARCEL_LAYER_STYLE.lineColor];
      lineWidth = ['case', matchExpr, FILTER_HIGHLIGHT_STYLE.lineWidth, PARCEL_LAYER_STYLE.lineWidth];
    } else {
      fillColor = PARCEL_LAYER_STYLE.fillColor;
      fillOpacity = PARCEL_LAYER_STYLE.fillOpacity;
      lineColor = PARCEL_LAYER_STYLE.lineColor;
      lineWidth = PARCEL_LAYER_STYLE.lineWidth;
    }

    // La capa de puntos ya viene filtrada por la misma expresión, así que todo
    // punto renderizado es una coincidencia: color plano, sin `case` por feature.
    let pointsCircleColor = PARCEL_POINTS_LAYER_STYLE.circleColor;

    if (myOpen && myIds.length > 0) {
      const myLiteral = ['literal', myIds];
      const inMine = ['in', ['get', 'id'], myLiteral];
      fillColor = ['case', inMine, MY_LOTS_HIGHLIGHT_STYLE.fillColor, fillColor];
      fillOpacity = [
        'case',
        ['all', inMine, ['boolean', ['feature-state', 'hover'], false]],
        MY_LOTS_HIGHLIGHT_STYLE.fillOpacityHover,
        inMine, MY_LOTS_HIGHLIGHT_STYLE.fillOpacity,
        fillOpacity,
      ];
      lineColor = ['case', inMine, MY_LOTS_HIGHLIGHT_STYLE.lineColor, lineColor];
      lineWidth = ['case', inMine, MY_LOTS_HIGHLIGHT_STYLE.lineWidth, lineWidth];
      pointsCircleColor = ['case', inMine, MY_LOTS_HIGHLIGHT_STYLE.lineColor, pointsCircleColor];
    } else if (listsOpen && (listIds.length > 0 || pendingAddIds.length > 0)) {
      if (listIds.length > 0) {
        const listLiteral = ['literal', listIds];
        const inList = ['in', ['get', 'id'], listLiteral];
        fillColor = ['case', inList, MY_LOTS_HIGHLIGHT_STYLE.fillColor, fillColor];
        fillOpacity = [
          'case',
          ['all', inList, ['boolean', ['feature-state', 'hover'], false]],
          MY_LOTS_HIGHLIGHT_STYLE.fillOpacityHover,
          inList, MY_LOTS_HIGHLIGHT_STYLE.fillOpacity,
          fillOpacity,
        ];
        lineColor = ['case', inList, MY_LOTS_HIGHLIGHT_STYLE.lineColor, lineColor];
        lineWidth = ['case', inList, MY_LOTS_HIGHLIGHT_STYLE.lineWidth, lineWidth];
        pointsCircleColor = ['case', inList, MY_LOTS_HIGHLIGHT_STYLE.lineColor, pointsCircleColor];
      }

      if (pendingAddIds.length > 0) {
        const pendingLiteral = ['literal', pendingAddIds.map(String)];
        const inPending = ['in', ['to-string', ['get', 'id']], pendingLiteral];
        fillColor = ['case', inPending, LIST_PENDING_ADD_HIGHLIGHT_STYLE.fillColor, fillColor];
        fillOpacity = ['case', inPending, LIST_PENDING_ADD_HIGHLIGHT_STYLE.fillOpacity, fillOpacity];
        lineColor = ['case', inPending, LIST_PENDING_ADD_HIGHLIGHT_STYLE.lineColor, lineColor];
        lineWidth = ['case', inPending, LIST_PENDING_ADD_HIGHLIGHT_STYLE.lineWidth, lineWidth];
        pointsCircleColor = ['case', inPending, LIST_PENDING_ADD_HIGHLIGHT_STYLE.lineColor, pointsCircleColor];
      }

      if (selectedListLotId) {
        const selectedStyle = selectedLotHighlightStyle || LIST_SELECTED_LOT_HIGHLIGHT_STYLE;
        const isSelected = ['==', ['get', 'id'], selectedListLotId];
        fillColor = ['case', isSelected, selectedStyle.fillColor, fillColor];
        fillOpacity = ['case', isSelected, selectedStyle.fillOpacity, fillOpacity];
        lineColor = ['case', isSelected, selectedStyle.lineColor, lineColor];
        lineWidth = ['case', isSelected, selectedStyle.lineWidth, lineWidth];
        pointsCircleColor = ['case', isSelected, selectedStyle.lineColor, pointsCircleColor];
      }
    }

    return {
      fillColor,
      fillOpacity,
      lineColor,
      lineWidth,
      pointsCircleColor,
      selectedLotId: selectedListLotId && listsOpen
        ? String(selectedListLotId)
        : null,
    };
  }, [buildMatchExpression]);

  const getCurrentPaintExpressions = useCallback(() => {
    const paint = buildPaintExpressions(
      activeFiltersRef.current,
      myLotsIds,
      isMyLotsOpen,
      listHighlightIds,
      isListsOpen,
      listSelectedLotId,
      listPendingAddIds,
      listSelectedLotHighlightStyle,
    );
    const matchExpr = buildMatchExpression(activeFiltersRef.current);
    return {
      ...paint,
      layerFilter: buildParcelLayerFilter({
        showOnly: showOnlyFiltered,
        matchExpr,
        clipToHighlightIds,
        listIds: clipToHighlightIds
          ? [...listHighlightIds, ...listPendingAddIds]
          : listHighlightIds,
      }),
    };
  }, [
    buildPaintExpressions,
    buildMatchExpression,
    myLotsIds,
    isMyLotsOpen,
    listHighlightIds,
    isListsOpen,
    listSelectedLotId,
    listPendingAddIds,
    listSelectedLotHighlightStyle,
    showOnlyFiltered,
    clipToHighlightIds,
  ]);

  const applyPointsToMap = useCallback((map, filters) => {
    if (!map) return;

    const ids = idsRef.current;
    const paint = buildPaintExpressions(
      filters,
      ids.myLotsIds,
      isMyLotsOpen,
      ids.listHighlightIds,
      isListsOpen,
      ids.listSelectedLotId,
      ids.listPendingAddIds,
      ids.listSelectedLotHighlightStyle,
    );
    const pointsLayerFilter = buildParcelLayerFilter({
      showOnly: true,
      matchExpr: buildMatchExpression(filters),
      clipToHighlightIds,
      listIds: clipToHighlightIds
        ? [...ids.listHighlightIds, ...ids.listPendingAddIds]
        : ids.listHighlightIds,
    });

    getParcelPointsLayerIds?.()?.filter((id) => map.getLayer(id)).forEach((id) => {
      map.setFilter(id, pointsLayerFilter);
      if (paint.pointsCircleColor) {
        map.setPaintProperty(id, 'circle-color', paint.pointsCircleColor);
      }
    });
  }, [
    buildPaintExpressions,
    buildMatchExpression,
    getParcelPointsLayerIds,
    isMyLotsOpen,
    isListsOpen,
    clipToHighlightIds,
  ]);

  const applyToMap = useCallback((
    filters,
    showOnly = showOnlyFiltered,
    myIds,
    myOpen = isMyLotsOpen,
    listIds,
    listsOpen = isListsOpen,
    selectedListLotIdArg = listSelectedLotId,
    pendingAddIds,
  ) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const ids = idsRef.current;
    const m = myIds ?? ids.myLotsIds;
    const l = listIds ?? ids.listHighlightIds;
    const selectedId = selectedListLotIdArg ?? ids.listSelectedLotId;
    const pending = pendingAddIds ?? ids.listPendingAddIds;
    const selectedStyle = ids.listSelectedLotHighlightStyle;

    const paint = buildPaintExpressions(
      filters,
      m,
      myOpen,
      l,
      listsOpen,
      selectedId,
      pending,
      selectedStyle,
    );
    const matchExpr = buildMatchExpression(filters);
    const layerFilter = buildParcelLayerFilter({
      showOnly,
      matchExpr,
      clipToHighlightIds,
      listIds: clipToHighlightIds
        ? [...l, ...pending]
        : l,
    });
    const fillLayerIds = getInteractiveParcelLayerIds().filter((id) => map.getLayer(id));
    const lineLayerIds = fillLayerIds
      .map((id) => id.replace('-fill-', '-layer-'))
      .filter((id) => map.getLayer(id));
    const selectedGlowFilter = buildSelectedLotIdFilter(
      listsOpen ? selectedId : null,
    );

    const applyPointsLayers = (targetMap) => applyPointsToMap(targetMap, filters);

    fillLayerIds.forEach((id) => {
      map.setFilter(id, layerFilter);
      map.setPaintProperty(id, 'fill-color', paint.fillColor);
      map.setPaintProperty(id, 'fill-opacity', paint.fillOpacity);
    });
    lineLayerIds.forEach((id) => {
      map.setFilter(id, layerFilter);
      map.setPaintProperty(id, 'line-color', paint.lineColor);
      map.setPaintProperty(id, 'line-width', paint.lineWidth);
    });
    fillLayerIds.forEach((fillId) => {
      const tilesetId = fillId.replace('self-hosted-vector-fill-', '');
      const glowId = parcelSelectedGlowLayerId(tilesetId);
      if (map.getLayer(glowId)) map.setFilter(glowId, selectedGlowFilter);
    });
    applyPointsLayers(map);

    const officialCount = countActiveOfficialUnitFilters(filters);
    const parcelCount = countActiveParcelFilters(filters) - officialCount;
    const officialHits = Array.isArray(officialUnitLotIdsRef.current)
      && officialUnitLotIdsRef.current.length > 0;
    // UF catastrales lejos / sin ids no encienden la muestra de puntos provincial:
    // esa nube no representa el padrón y pinta decenas de miles de centroides.
    const hasAttributeFilters = parcelCount > 0 || officialHits;
    syncParcelPointsView?.(map, {
      // Con el alcance bloqueado por un panel, los pins salen de getmylots,
      // no de la muestra de teselas de puntos.
      hasActiveFilters: (hasAttributeFilters || shouldApplyScope(filters)) && !panelLockRef.current,
      onPointsLayerReady: (readyMap) => applyPointsLayers(readyMap),
    });
  }, [
    mapInstanceRef,
    getInteractiveParcelLayerIds,
    applyPointsToMap,
    syncParcelPointsView,
    buildPaintExpressions,
    buildMatchExpression,
    shouldApplyScope,
    showOnlyFiltered,
    clipToHighlightIds,
    isMyLotsOpen,
    isListsOpen,
    listSelectedLotId,
  ]);

  const listSelectedLotIdKey = listSelectedLotId ? String(listSelectedLotId) : '';

  useEffect(() => {
    if (!reapplyPointsFiltersRef) return undefined;
    reapplyPointsFiltersRef.current = () => {
      const map = mapInstanceRef.current;
      if (map) applyPointsToMap(map, activeFiltersRef.current);
    };
    return () => {
      reapplyPointsFiltersRef.current = null;
    };
  }, [applyPointsToMap, reapplyPointsFiltersRef, mapInstanceRef]);

  useEffect(() => {
    applyToMap(activeFiltersRef.current);
  }, [
    isMyLotsOpen,
    isListsOpen,
    showOnlyFiltered,
    clipToHighlightIds,
    applyToMap,
    myLotsIdsKey,
    listHighlightIdsKey,
    listPendingAddIdsKey,
    listSelectedLotIdKey,
    listSelectedLotHighlightStyle,
    scopeIdsKey,
    isScopeActive,
    scopeLockedByPanel,
    officialUnitLotIds,
  ]);

  // Con alcance activo hay filtros que `filter_attributes` no puede evaluar
  // (built / altura_max_m): el panel los oculta y acá se limpia el valor que
  // hubieran dejado, para que los pins de zoom bajo y los polígonos de zoom alto
  // no muestren conjuntos distintos.
  useEffect(() => {
    if (!isScopeActive) return;
    setActiveFilters((prev) => {
      const next = clearScopeUnsupportedFilters(prev);
      if (next === prev) return prev;
      applyToMap(next);
      return next;
    });
  }, [isScopeActive, applyToMap]);

  // Los filtros de publicación solo tienen sentido con un universo de lotes
  // propios. Al volver a "todos los lotes" se descartan para no dejar un
  // estado invisible que vaciaría el mapa.
  useEffect(() => {
    if (isScopeActive) return;
    setActiveFilters((prev) => {
      const next = clearListingFilters(prev);
      if (next === prev) return prev;
      applyToMap(next);
      return next;
    });
  }, [isScopeActive, applyToMap]);

  useEffect(() => {
    if (officialUnitFiltersEnabled) return;
    setActiveFilters((prev) => {
      const next = clearOfficialUnitFilters(prev);
      if (next === prev) return prev;
      applyToMap(next);
      return next;
    });
  }, [officialUnitFiltersEnabled, applyToMap]);

  const setFilterValue = useCallback((field, value) => {
    setActiveFilters((prev) => {
      const next = { ...prev, [field]: value };
      applyToMap(next);
      return next;
    });
  }, [applyToMap]);

  const removeFilterChip = useCallback((chip) => {
    setActiveFilters((prev) => {
      const def = getActiveFilterDefinitions().find((d) => d.field === chip.field);
      if (!def) return prev;

      let nextValue = null;
      const current = prev[chip.field];

      if (def.component === 'multiSelect' && chip.removeValue != null) {
        const remaining = (current || []).filter((v) => v !== chip.removeValue);
        nextValue = remaining.length ? remaining : null;
      } else if (def.component === 'rangeBands' && chip.removeValue != null) {
        const remaining = (current || []).filter((v) => v !== chip.removeValue);
        nextValue = remaining.length ? remaining : null;
      } else {
        nextValue = null;
      }

      const next = { ...prev, [chip.field]: nextValue };
      applyToMap(next);
      return next;
    });
  }, [applyToMap]);

  /** @deprecated usar setFilterValue */
  const toggleDiscreteFilter = useCallback((field, value) => {
    setFilterValue(field, [value]);
  }, [setFilterValue]);

  /** @deprecated usar setFilterValue */
  const setRangeFilter = useCallback((field, range) => {
    setFilterValue(field, range);
  }, [setFilterValue]);

  /** @deprecated usar setFilterValue('surface', range) */
  const setSurfaceFilter = useCallback((range) => {
    setFilterValue('surface', range);
  }, [setFilterValue]);

  const clearAllFilters = useCallback(() => {
    const cleared = createEmptyParcelFilters();
    setActiveFilters(cleared);
    setShowOnlyFiltered(false);
    applyToMap(cleared, false);
  }, [applyToMap]);

  /**
   * Replace de filtros del tab Lote (parse NL). No toca listing ni UF oficiales.
   * No prende “solo filtradas”: el usuario lo activa si quiere ocultar el resto.
   */
  const applyLotFiltersFromParse = useCallback((parsedFilters) => {
    setActiveFilters((prev) => {
      const next = { ...clearParcelFilters(prev), ...parsedFilters };
      applyToMap(next, showOnlyFiltered);
      return next;
    });
  }, [applyToMap, showOnlyFiltered]);

  /**
   * Replace de Lote + Publicación (parse NL de lista compartida).
   * Solo cuando `schema_version` es `shared-list-v1`; si no, usar `applyLotFiltersFromParse`.
   * No prende “solo filtradas”. No toca UF catastrales.
   */
  const applySharedListFiltersFromParse = useCallback((parsedFilters) => {
    setActiveFilters((prev) => {
      const {
        listing_currency: listingCurrency,
        listing_price: listingPrice,
        ...rest
      } = parsedFilters || {};
      const next = {
        ...clearListingFilters(clearParcelFilters(prev)),
        ...rest,
      };
      // Moneda antes que precio. El clear ya tiró cortes viejos: si cambia
      // la moneda y no vino listing_price, no se reutilizan.
      if (listingCurrency != null) next.listing_currency = listingCurrency;
      if (listingPrice != null) next.listing_price = listingPrice;
      applyToMap(next, showOnlyFiltered);
      return next;
    });
  }, [applyToMap, showOnlyFiltered]);

  const toggleShowOnlyFiltered = useCallback(() => {
    setShowOnlyFiltered((prev) => {
      const next = !prev;
      applyToMap(activeFiltersRef.current, next);
      return next;
    });
  }, [applyToMap]);

  const matchExpression = useMemo(() => {
    // Fuerza recálculo cuando cambia el alcance: buildMatchExpression lo lee de refs.
    void scopeIdsKey;
    void isScopeActive;
    void scopeLockedByPanel;
    void officialUnitLotIds;
    return buildMatchExpression(activeFilters);
  }, [buildMatchExpression, activeFilters, scopeIdsKey, isScopeActive, scopeLockedByPanel, officialUnitLotIds]);

  return {
    activeFilters,
    activeCount,
    hasActiveFilters,
    matchExpression,
    showOnlyFiltered,
    setFilterValue,
    removeFilterChip,
    toggleDiscreteFilter,
    setRangeFilter,
    setSurfaceFilter,
    clearAllFilters,
    applyLotFiltersFromParse,
    applySharedListFiltersFromParse,
    toggleShowOnlyFiltered,
    getCurrentPaintExpressions,
  };
}
